import React, { useEffect, useState } from "react";
const API = process.env.REACT_APP_API_BASE_URL || "https://zoompay.co.ke";
const getToken = () => localStorage.getItem("adminToken");

export default function CockpitTransactions() {
  const [txns,     setTxns]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [filter,   setFilter]   = useState({ type: "", status: "", fundingStatus: "", payoutStatus: "" });
  const [selected, setSelected] = useState(null);
  const [offset, setOffset] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(25);

  // Action state
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg,     setActionMsg]     = useState(null); // { type: 'success'|'error', text: string }

  const load = async () => {
    setLoading(true);
    const q = new URLSearchParams({
      limit,
      offset,
      ...(search ? { search } : {}),
      ...Object.fromEntries(Object.entries(filter).filter(([, v]) => v))
    });
    const res = await fetch(`${API}/api/v2/cockpit/transactions?${q}`, {
      headers: { Authorization: `Bearer ${getToken()}` }
    });
    const d = await res.json();
    if (d.ok) { setTxns(d.transactions || d.data || []); setTotal(d.total || 0); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filter, offset, search, limit]);
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setOffset(0);
    setSearch(searchInput.trim());
  };

  const statusColor = (s) => {
    if (!s) return "text-gray-500";
    const v = s.toLowerCase();
    if (v === "completed")  return "text-green-600 font-semibold";
    if (v === "pending")    return "text-yellow-600 font-semibold";
    if (v === "failed")     return "text-red-600 font-semibold";
    return "text-gray-600";
  };

  const openTransaction = async (txn) => {
    setActionMsg(null);

    if (txn.transactionType === "SNPL" && txn.snplSettlementId) {
      try {
        const res = await fetch(`${API}/api/v2/cockpit/settlements/${txn.snplSettlementId}`, {
          headers: { Authorization: `Bearer ${getToken()}` }
        });

        const d = await res.json();

        if (d.ok && d.settlement) {
          setSelected({ ...txn, snplSettlement: d.settlement });
          return;
        }
      } catch (e) {
        console.error("Failed to fetch settlement detail:", e);
      }
    }

    setSelected(txn);
  };

  // ── Simulate I&M payout callback ─────────────────────────────────────
  const simulatePayout = async (txn) => {
    if (!window.confirm(`Simulate I&M payout confirmation for transaction ${txn.id.substring(0, 8)}...?\n\nThis will mark the transaction as COMPLETED.`)) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/transactions/${txn.id}/simulate-payout`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" }
      });
      const d = await res.json();
      if (d.ok) {
        setActionMsg({ type: "success", text: "✅ Payout simulated — transaction marked COMPLETED" });
        // Update local state immediately
        setSelected(prev => ({ ...prev, status: "completed", payoutStatus: "COMPLETED", overallStatus: "COMPLETED" }));
        setSelected(prev => ({
          ...prev,
          status: "completed",
          fundingStatus: "COMPLETED",
          fundingProvider: "INTERNAL",
          payoutStatus: "COMPLETED",
          payoutProvider: "IM_BANK",
          snplSettlement: {
            ...(prev.snplSettlement || {}),
            status: "paid",
            remainingAmount: 0,
            paidAmount: parseFloat(prev.snplSettlement?.totalAmount || prev.snplSettlement?.total_amount || 0)
          }
        }));

        setTxns(prev => prev.map(t => t.id === txn.id ? {
          ...t,
          status: "completed",
          fundingStatus: "COMPLETED",
          fundingProvider: "INTERNAL",
          payoutStatus: "COMPLETED",
          payoutProvider: "IM_BANK",
          snplSettlement: {
            ...(t.snplSettlement || {}),
            status: "paid",
            remainingAmount: 0
          }
        } : t));
      } else {
        setActionMsg({ type: "error", text: `❌ ${d.error || "Simulation failed"}` });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: `❌ Network error: ${e.message}` });
    } finally {
      setActionLoading(false);
    }
  };

  // ── Mark SNPL settlement paid (mimics Zum Rails EFT webhook) ─────────
  const markSnplPaid = async (txn) => {
    if (!txn.snplSettlementId) {
      setActionMsg({ type: "error", text: "❌ No settlement ID found for this transaction" });
      return;
    }
    if (!window.confirm(`Mark SNPL settlement as PAID for ${txn.user?.firstName} ${txn.user?.lastName}?\n\nThis mimics a Zum Rails EFT/Interac confirmation and will reset their SNPL credit.`)) return;
    setActionLoading(true);
    setActionMsg(null);
    try {
      const res = await fetch(`${API}/api/v2/cockpit/settlements/${txn.snplSettlementId}/mark-paid`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "application/json" }
      });
      const d = await res.json();
      if (d.ok) {
        setActionMsg({
          type: "success",
          text: `✅ SNPL settlement marked paid. New SNPL available: $${parseFloat(d.newSnplAvailable || 0).toFixed(2)} CAD`
        });
        setTxns(prev => prev.map(t => t.id === txn.id ? { ...t, status: "completed" } : t));
        setSelected(prev => prev && prev.id === txn.id ? {
          ...prev,
          status: "completed",
          fundingStatus: "COMPLETED",
          payoutStatus: "COMPLETED",
          snplSettlement: { ...prev.snplSettlement, status: "paid", remainingAmount: 0 }
        } : prev);
      } else {
        setActionMsg({ type: "error", text: `❌ ${d.error || "Mark paid failed"}` });
      }
    } catch (e) {
      setActionMsg({ type: "error", text: `❌ Network error: ${e.message}` });
    } finally {
      setActionLoading(false);
    }
  };


  // ── Close modal & reset action state ─────────────────────────────────
  const closeModal = () => {
    setSelected(null);
    setActionMsg(null);
  };

  return (
    <div className="p-6 space-y-4">

      {/* ── Transaction Detail Modal ── */}
      {selected && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full mx-4 max-h-screen overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Transaction Details</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
            </div>

            {/* Action feedback message */}
            {actionMsg && (
              <div className={`mb-4 p-3 rounded text-sm font-medium ${
                actionMsg.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}>
                {actionMsg.text}
              </div>
            )}

            {/* Details rows */}
            <div className="space-y-2 text-sm mb-6">
              {[
                ["ID",           <span className="font-mono text-xs">{selected.id}</span>],
                ["Type",         <span className={`px-2 py-0.5 rounded text-xs font-bold ${selected.transactionType === "SNPL" ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}`}>{selected.transactionType}</span>],
                ["User",         `${selected.user?.firstName} ${selected.user?.lastName} (${selected.user?.email})`],
                ["Date",         new Date(selected.createdAt).toLocaleString()],
                ["Reference",    (() => { const p = selected.transactionType === "SNPL" ? "SNPL" : "SNPN"; return selected.reference || p + "-" + selected.id.replace(/-/g, "").substring(0, 6).toUpperCase(); })()],
                ["Send Amount",  `$${parseFloat(selected.senderAmount).toFixed(2)} ${selected.senderCurrency}`],
                ["Exchange Fee (2.25%)", `$${parseFloat(selected.variableFee || 0).toFixed(2)} ${selected.senderCurrency}`],
                ...(selected.transactionType === "SNPL" ? [
                  ["SNPL Service Fee",    `$${parseFloat(selected.snplSettlement?.serviceFee || 0).toFixed(2)} ${selected.senderCurrency}`],
                  ["Extension Fees",      `$${parseFloat(selected.snplSettlement?.extensionFeesTotal || 0).toFixed(2)} ${selected.senderCurrency}`],
                  ["Total Customer Owes", `$${parseFloat(selected.snplSettlement?.totalAmount || (parseFloat(selected.senderAmount || 0) + parseFloat(selected.snplSettlement?.serviceFee || 0) + parseFloat(selected.snplSettlement?.extensionFeesTotal || 0))).toFixed(2)} ${selected.senderCurrency}`],
                  ["Settlement Due",      selected.snplSettlement?.dueDate ? new Date(selected.snplSettlement.dueDate).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" }) : "N/A"],
                  ["Settlement Status",   <span className={`px-2 py-0.5 rounded text-xs font-bold ${selected.snplSettlement?.status === "paid" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}`}>{(selected.snplSettlement?.status || "N/A").toUpperCase()}</span>],
                ] : []),
                ["Recipient Gets", `${parseFloat(selected.recipientAmount).toLocaleString()} ${selected.recipientCurrency}`],
                ["Exchange Rate",  `1 ${selected.senderCurrency} = ${parseFloat(selected.exchangeRate).toFixed(4)} ${selected.recipientCurrency}`],
                ["Recipient",      `${selected.recipientName} (${selected.recipientPhone})`],
                ["Corridor",       selected.corridor || `${selected.senderCurrency}→${selected.recipientCurrency}`],
                ["Funding",        `${selected.fundingStatus || "N/A"} via ${selected.fundingProvider || "N/A"}`],
                ["Payout",         `${selected.payoutStatus || "N/A"} via ${selected.payoutProvider || "N/A"}`],
                ["Status",         <span className={`font-bold ${statusColor(selected.status)}`}>{selected.status?.toUpperCase()}</span>],
                ...(selected.snplSettlementId ? [["Settlement ID", <span className="font-mono text-xs">{selected.snplSettlementId}</span>]] : []),
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between border-b pb-1">
                  <span className="text-gray-500 w-1/3">{label}</span>
                  <span className="text-right w-2/3">{val}</span>
                </div>
              ))}
            </div>

            {/* ── Action Buttons ── */}
            <div className="border-t pt-4 space-y-2">
              <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide mb-2">Admin Actions</p>

              {/* Simulate I&M Payout — shown for non-completed transactions */}
              {selected.status !== "completed" && (
                <button
                  onClick={() => simulatePayout(selected)}
                  disabled={actionLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 px-4 rounded text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {actionLoading ? "Processing..." : "🏦 Simulate I&M Payout Confirmation"}
                </button>
              )}

              {/* Mark SNPL Paid — shown for SNPL with unpaid settlement */}
              {selected.transactionType === "SNPL" && selected.snplSettlementId && selected.snplSettlement?.status !== "paid" && (
                <button
                  onClick={() => markSnplPaid(selected)}
                  disabled={actionLoading}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2 px-4 rounded text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {actionLoading ? "Processing..." : "💰 Simulate SNPL Payment (Zum Rails EFT)"}
                </button>
              )}

              {/* Already completed */}
              {selected.status === "completed" && selected.snplSettlement?.status !== "paid" && selected.transactionType !== "SNPL" && (
                <p className="text-center text-sm text-green-600 font-medium">✅ Transaction already completed</p>
              )}

              {selected.status === "completed" && selected.transactionType === "SNPL" && selected.snplSettlement?.status === "paid" && (
                <p className="text-center text-sm text-green-600 font-medium">✅ Transaction and settlement both completed</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ── */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">💸 Transactions</h1>
          <p className="text-gray-500 text-sm">{total} total transactions</p>
        </div>
        <div className="flex gap-3">
          <select
            className="border rounded px-3 py-2 text-sm"
            value={filter.type}
            onChange={e => { setOffset(0); setFilter(f => ({ ...f, type: e.target.value })); }}
          >
            <option value="">All Types</option>
            <option value="SNPN">SNPN</option>
            <option value="SNPL">SNPL</option>
          </select>
          <select
            className="border rounded px-3 py-2 text-sm"
            value={filter.status}
            onChange={e => { setOffset(0); setFilter(f => ({ ...f, status: e.target.value })); }}
          >
            <option value="">All ZP Statuses</option>
            <option value="completed">ZP Completed</option>
            <option value="pending">ZP Pending</option>
            <option value="processing">ZP Processing</option>
            <option value="failed">ZP Failed</option>
          </select>
          <select
            className="border rounded px-3 py-2 text-sm"
            value={filter.fundingStatus}
            onChange={e => { setOffset(0); setFilter(f => ({ ...f, fundingStatus: e.target.value })); }}
          >
            <option value="">All Funding</option>
            <option value="PENDING">Funding Pending</option>
            <option value="COMPLETED">Funding Completed</option>
            <option value="FAILED">Funding Failed</option>
          </select>
          <select
            className="border rounded px-3 py-2 text-sm"
            value={filter.payoutStatus}
            onChange={e => { setOffset(0); setFilter(f => ({ ...f, payoutStatus: e.target.value })); }}
          >
            <option value="">All Payouts</option>
            <option value="PENDING">Payout Pending</option>
            <option value="COMPLETED">Payout Completed</option>
            <option value="FAILED">Payout Failed</option>
          </select>
          <form onSubmit={handleSearchSubmit} className="flex items-center gap-2">
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search reference..."
              className="border rounded px-3 py-2 text-sm w-48"
            />
            <button type="submit" className="px-3 py-2 text-sm rounded border bg-white hover:bg-gray-50">
              Search
            </button>
          </form>
          <button
            onClick={() => {
              setOffset(0);
              setSearch(searchInput.trim());
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* ── Pagination Controls ── */}
      <div className="flex items-center justify-between bg-white border rounded-lg p-3 text-sm">
        <div className="text-gray-600">
          Showing {total === 0 ? 0 : offset + 1}
          - {Math.min(offset + limit, total)} of {total}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-gray-600">Rows:</span>
          <select
            value={limit}
            onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
            className="border rounded px-2 py-1"
          >
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>

          <button
            onClick={() => setOffset(o => Math.max(0, o - limit))}
            disabled={offset <= 0}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Prev
          </button>

          <span className="text-gray-700">
            Page {Math.floor(offset / limit) + 1} of {Math.max(1, Math.ceil(total / limit))}
          </span>

          <button
            onClick={() => setOffset(o => o + limit)}
            disabled={offset + limit >= total}
            className="px-3 py-1 border rounded disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      {/* ── Transactions Table ── */}
      <div className="bg-white border rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {["Date","Reference","User","Type","Send","Owed","Gets","Corridor","Funding","Payout","ZP","Action"].map(h => (
                <th key={h} className="px-3 py-3 text-left text-gray-600 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading && (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && txns.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-8 text-center text-gray-400">No transactions found</td></tr>
            )}
            {txns.map(t => {
              const isSNPL    = t.transactionType === "SNPL";
              const totalOwed = isSNPL
                ? parseFloat(
                    t.snplSettlement?.remainingAmount ||
                    t.snplSettlement?.remaining_amount ||
                    t.snplSettlement?.totalAmount ||
                    t.snplSettlement?.total_amount ||
                    t.senderAmount ||
                    0
                  ).toFixed(2)
                : parseFloat(t.senderAmount || 0).toFixed(2);
              return (
                <tr key={t.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3 text-gray-500 whitespace-nowrap">
                    {new Date(t.createdAt).toLocaleDateString("en-CA")}
                  </td>
                  <td className="px-3 py-3 font-mono text-xs whitespace-nowrap">
                    {t.reference || "—"}
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-medium">{t.user?.firstName} {t.user?.lastName}</div>
                    <div className="text-xs text-gray-400">{t.user?.email}</div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${isSNPL ? "bg-yellow-100 text-yellow-800" : "bg-blue-100 text-blue-800"}`}>
                      {t.transactionType}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-semibold">${parseFloat(t.senderAmount).toFixed(2)} {t.senderCurrency}</td>
                  <td className="px-3 py-3 font-semibold text-blue-800">${totalOwed} {t.senderCurrency}</td>
                  <td className="px-3 py-3 text-green-700 font-semibold">
                    {parseFloat(t.recipientAmount).toLocaleString()} {t.recipientCurrency}
                  </td>
                  <td className="px-3 py-3 text-gray-600">
                    {t.corridor || `${t.senderCurrency}→${t.recipientCurrency}`}
                  </td>
                  <td className={`px-4 py-3 ${statusColor(t.fundingStatus)}`}>
                    {t.fundingStatus || "—"}
                  </td>
                  <td className={`px-4 py-3 ${statusColor(t.payoutStatus)}`}>
                    {t.payoutStatus || "—"}
                  </td>
                  <td className={`px-4 py-3 ${statusColor(t.status)}`}>
                    {t.status?.toUpperCase()}
                  </td>
                  <td className="px-3 py-3 sticky right-0 bg-white shadow-l">
                    <button
                      onClick={() => openTransaction(t)}
                      className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 whitespace-nowrap"
                    >
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
